import anime from 'animejs/lib/anime.es.js'

import { Block } from '@/core/Block.js'
import { Sleep } from '@/utils/Sleep.js'
import { Config } from '@/core/Config.js'
import { Events, EventType } from '@/utils/Events'
import { isMobile } from '../utils/Support'

export class Loader extends Block {
  static hidden = false

  static async init() {
    // Body opacity is already removed by critical inline JS to ensure FCP
    this.el = document.body.querySelector('.c-loader')
    this.logo = document.body.querySelector('.c-loader-logo')

    if (Config.LOADER) {
      this.intialLoad()
    } else {
      // Hide loader element immediately (no animation needed when LOADER is disabled).
      if (this.el) this.el.style.display = 'none'

      // Use a short setTimeout to yield to the browser before emitting LOADER_DONE.
      // This serves two purposes:
      // 1. Allows App.init() to finish Router/View setup (the View registers
      //    its LOADER_DONE listener during init, which runs after Loader.init())
      // 2. Breaks initialization into separate tasks to reduce TBT —
      //    queueMicrotask batches everything into one long task
      setTimeout(() => {
        if (!Loader.hidden) {
          Loader.hidden = true
          Events.emit(EventType.LOADER_DONE)
        }
      }, 0)
    }
  }

  static async intialLoad() {
    await Sleep(200)

    if (!Config.PREVIEW) {
      this.show()

      if (!isMobile()) {
        await Sleep(2000)
      }
    }

    this.hide()

    if (!Loader.hidden) {
      Loader.hidden = true
      Events.emit(EventType.LOADER_DONE)
    }
  }

  static async show() {
    this.animateInTl && this.animateInTl.pause()
    this.rotateTl && this.rotateTl.pause()

    this.loaderIcon = document.body.querySelector('.c-loader-icon')
    this.loaderIconWrapper = document.body.querySelector('.c-loader-icon-wrapper')
    this.loaderIconSymbol1 = document.body.querySelector('.c-loader-icon-symbol--1')
    this.loaderIconSymbol2 = document.body.querySelector('.c-loader-icon-symbol--2')

    this.animateInTl = anime.timeline()
    this.rotateTl = anime.timeline({ loop: true, autoplay: false })

    this.animateInTl.add({
      targets: this.el,
      opacity: 1,
      duration: 500,
      easing: 'linear',
    })

    this.animateInTl.add(
      {
        targets: this.logo,
        opacity: 1,
        duration: 500,
        easing: 'linear',
      },
      0
    )

    this.animateInTl.add(
      {
        targets: this.loaderIconWrapper,
        scale: [0.85, 1],
        opacity: [0, 1],
        duration: 1200,
        easing: 'easeOutExpo',
        begin: () => {
          this.rotateTl.play()
        },
      },
      0
    )

    const params = {
      targets: this.loaderIcon,
      easing: 'spring(0.75, 60, 12, 6)',
      duration: 1000,
    }

    this.rotateTl.add({
      targets: this.loaderIcon,
      rotate: ['0deg', '90deg'],
      ...params,
      changeBegin: () => {
        this.loaderIcon.classList.add('-alt')
      },
    })

    this.rotateTl.add({
      targets: this.loaderIcon,
      rotate: ['90deg', '180deg'],
      ...params,
      changeBegin: () => {
        this.loaderIcon.classList.remove('-alt')
      },
    })

    this.rotateTl.add({
      targets: this.loaderIcon,
      rotate: ['180deg', '270deg'],
      ...params,
      changeBegin: () => {
        this.loaderIcon.classList.add('-alt')
      },
    })

    this.rotateTl.add({
      targets: this.loaderIcon,
      rotate: ['270deg', '360deg'],
      ...params,
      changeBegin: () => {
        this.loaderIcon.classList.remove('-alt')
      },
    })
  }

  static async hide(keepRotating = false, force = false) {
    this.animateInTl && this.animateInTl.pause()
    if (!keepRotating) this.rotateTl && this.rotateTl.pause()

    if (!force) {
      anime({
        targets: this.loaderIconWrapper,
        scale: [1, 0.85],
        opacity: 0,
        duration: 500,
        easing: 'easeInExpo',
      })

      anime({
        targets: this.logo,
        opacity: 0,
        duration: 300,
        delay: 500,
        easing: 'linear',
      })
    }

    anime({
      targets: this.el,
      opacity: 0,
      delay: force ? 0 : 500,
      duration: force ? 300 : 500,
      easing: 'linear',
      complete: () => {
        if (keepRotating) this.rotateTl && this.rotateTl.pause()
      },
    })
  }
}
